import { CompanyRequestError } from '@/company/api';
import { FILE_TYPES } from '../../src/company-workspace/data/business-file-types.mjs';
import {
  LEGACY_UPLOAD_BYTES,
  MAX_UPLOAD_BYTES,
  UPLOAD_RULE,
  uploadErrorMessage,
  uploadMime,
  workspaceApi,
} from './api';
import { directUploadFile, paymentRequired, storageUnavailable } from './direct-upload';
import { workspaceErrorCopy } from './workspace-errors';
import type { CompanyFile } from './types';

/** A refused upload in words; a payment refusal says the server's own sentence (plan, credits or limit). */
export const uploadRefusalMessage = (name: string, error: CompanyRequestError, locale?: string) =>
  paymentRequired(error)
    ? error.message
    : uploadErrorMessage(name, error.status, error.code, locale);

/** The server refused an upload; `message` is for people, `code` for callers that act on the reason. */
export class WorkspaceUploadRefused extends Error {
  constructor(
    message: string,
    readonly code: string,
  ) {
    super(message);
    this.name = 'WorkspaceUploadRefused';
  }
}

/** One upload path for Library and composer: the server owns scope and extraction. */
export async function uploadWorkspaceFile(
  file: File,
  includeInKnowledge = false,
  {
    onProgress,
    signal,
    onSession,
    locale,
  }: {
    /** The share of the file's bytes sent, 0 to 1. */
    onProgress?: (progress: number) => void;
    signal?: AbortSignal;
    /** Called with a way to discard the upload session once the server has opened it (null once it is gone). */
    onSession?: (discard: (() => Promise<void>) | null) => void;
    locale?: string;
  } = {},
): Promise<CompanyFile> {
  const mimeType = uploadMime(file.name);
  if (!mimeType || file.size > MAX_UPLOAD_BYTES)
    throw new Error(workspaceErrorCopy(locale).uploadRule);
  if (file.size === 0)
    throw new Error(workspaceErrorCopy(locale).emptyFile.replace('{name}', file.name));
  try {
    return (
      await directUploadFile({ file, mimeType, includeInKnowledge, onProgress, signal, onSession })
    ).file;
  } catch (error) {
    if (
      storageUnavailable(error) &&
      file.size <= LEGACY_UPLOAD_BYTES &&
      FILE_TYPES.includes(mimeType)
    ) {
      const stored = await workspaceApi.upload({ file, mimeType, includeInKnowledge, signal });
      onProgress?.(1);
      return stored;
    }
    if (error instanceof CompanyRequestError)
      throw new WorkspaceUploadRefused(uploadRefusalMessage(file.name, error, locale), error.code);
    throw error;
  }
}

export function fileReadabilityMessage(file: CompanyFile, locale?: string) {
  if (!file.processingStatus || file.processingStatus === 'ready') return '';
  const reason = file.failureCode
    ? uploadErrorMessage(file.name, 422, file.failureCode, locale)
    : '';
  return `${file.name} is saved privately for download, but Poneva cannot read its contents. ${reason || 'Upload a text version to use it in chat.'}`;
}
